import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
  Button,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import apiClient, { API_BASE } from "helpers/apiClient"
import { decodeDraftRawToReact } from "helpers/draftRawToReact"
import { getStatesOptions, SelectOption } from "helpers/dropdownsApi"
import { getCategoryOptionsForUser, getFormTypeOptionsForUser, canShowAdminForm } from "config/listingCategoryFields"
import { getUserApi, getPropertyDetailGateReason } from "helpers/auth_api"
import type { PropertyKycBlockReason } from "helpers/auth_api"
import UserDetailsModal, { UserDetailsModalUser } from "components/UserDetailsModal"
import KycRequiredModal from "components/KycRequiredModal/KycRequiredModal"
import { formatBidPrice, formatCategoryFieldDisplay, formatDisplayNumber, isNumericDisplayValue } from "helpers/indianNumber"
import { formatIndianDate, formatIndianDateTime } from "helpers/indianDate"
import "./Listings.scss"

const GET_LISTINGS_URL = "/api/listings/getListings"
const statusOptions = [
  { value: "", label: "All" },
  { value: "Vacant", label: "Vacant" },
  { value: "Pre-Rented", label: "Pre-Rented" },
  { value: "Rented", label: "Rented" },
  { value: "Operational", label: "Operational" },
  { value: "Vacant or Non-Operational", label: "Vacant or Non-Operational" },
]

/** categorySpecific fields (vary by listing category); known keys from API */
interface ListingCategorySpecific {
  occupancyStatus?: string
  admin_submittedData?: string
  admin_yearBuiltRenovated?: string
  admin_addTags?: string
  admin_shortPropertyDescription?: string
  admin_propertyDescription?: string
  admin_ribbonText?: string
  admin_status?: string
  admin_listingToggles?: string
  admin_assignToAuctionAdvisor?: string
  superArea?: string
  carpetAreaInputMethod?: string
  carpetAreaPercentSqFt?: string
  totalFloorsInBuilding?: string
  floorNumber?: string
  unitNo?: string
  isCarParkingIncluded?: string
  numberOfCarParks?: string
  parkingType?: string
  tenantName?: string
  tenantProfile?: string
  leaseStartEndDate?: string
  lockInPeriod?: string
  securityDeposit?: string
  rentEscalationClause?: string
  noticePeriod?: string
  rentalPerSqFtMonthly?: string
  currentMonthlyRental?: string
  desiredRentalYieldRoiPercent?: string
  totalSalePrice?: string
  pricePerSqFt?: string
  isMaintenancePaidByTenant?: string
  monthlyMaintenanceCharges?: string
  saleDeed?: string
  approvalsInPlace?: string
  superBuiltUpArea?: string | number
  carpetArea?: string | number
  [key: string]: unknown
}

interface Listing {
  _id: string
  listingCategoryType?: string
  category?: string
  status?: string
  propertyTitle?: string
  buildingName?: string
  address?: string
  roadName?: string
  state?: string
  city?: string
  pinCode?: string
  microMarketLocality?: string
  latitude?: number
  longitude?: number
  propertyTaxReceiptsUrls?: string[]
  categorySpecific?: ListingCategorySpecific
  propertyDescription?: string
  propertyImagesUrls?: string[]
  biddingEnabled?: boolean
  biddingStartPrice?: number
  biddingMinIncrement?: number
  auctionStartDate?: string
  auctionEndDate?: string
  createdAt?: string
  updatedAt?: string
  __v?: number
  auctionStatus?: "upcoming" | "live" | "ended"
  isUpcoming?: boolean
  /** User who created the listing (populated: fullName, role) */
  createdBy?: { _id: string; fullName?: string; role?: string } | null
  /** Root-level (legacy) */
  superAreaSqFt?: number
  carpetAreaSqFt?: number
  totalCarpetAreaSqFt?: number
  floorNumber?: number
  totalFloorsInBuilding?: number
  unitNo?: string
  isCarParkingIncluded?: string
  constructionStatus?: string
  furnishingStatus?: string
  totalSalePrice?: number | string
  pricePerSqFt?: number
  propertyTaxAnnual?: number
  monthlyMaintenanceCharges?: number
  leaseType?: string
  leaseStartDate?: string
  leaseEndDate?: string
  leaseDurationMonths?: number
  monthlyRent?: number
  securityDeposit?: number
  occupancyStatus?: string
  lockInPeriodMonths?: number
  expectedRentalYield?: number
  annualAppreciation?: number
  capRate?: number
  investmentAmount?: number
  expectedReturnYears?: number
  roiPercentage?: number
  titleDeedUrl?: string
  titleDeedConveyanceDeedUrl?: string
  occupancyCertificateOcUrl?: string
  latestTaxReceiptsUrl?: string
  leaseAgreementUrl?: string
  vastuReportUrl?: string
  floorPlanUrl?: string
  floorPlanSiteLayoutUrl?: string
  buildingBrochureUrl?: string
  generatedFlyerPdfUrl?: string
  biddingEndDate?: string
  [key: string]: any
}

interface BreadcrumbItem {
  title: string
  link: string
}

interface ListingsProps {
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

const breadcrumbItems: BreadcrumbItem[] = [
  { title: "Listings", link: "#" },
  { title: "All Listings", link: "#" },
]

const Listings = (props: ListingsProps) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const createdByFromUrl = searchParams.get("createdBy") || ""

  const LIMIT = 10
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null)
  const [page, setPage] = useState(1)
  const [stateOptions, setStateOptions] = useState<SelectOption[]>([])
  const [filters, setFilters] = useState({ category: "", status: "", city: "", state: "", listingCategoryType: "", createdBy: createdByFromUrl })
  const [appliedFilters, setAppliedFilters] = useState({ category: "", status: "", city: "", state: "", listingCategoryType: "", createdBy: createdByFromUrl })
  const [pagination, setPagination] = useState({ page: 1, limit: LIMIT, total: 0, totalPages: 1 })
  const [imageViewerIndex, setImageViewerIndex] = useState<number | null>(null)
  const [documentViewer, setDocumentViewer] = useState<{ url: string; title: string } | null>(null)
  const [userDetailsModalUser, setUserDetailsModalUser] = useState<UserDetailsModalUser | null>(null)
  const [userDetailsModalOpen, setUserDetailsModalOpen] = useState(false)
  const [userDetailsModalLoading, setUserDetailsModalLoading] = useState(false)
  const [generatingFlyerId, setGeneratingFlyerId] = useState<string | null>(null)
  const [kycModalOpen, setKycModalOpen] = useState(false)
  const [kycModalVariant, setKycModalVariant] = useState<PropertyKycBlockReason>("upload")
  const userRole = useMemo(() => {
    try {
      const raw = localStorage.getItem("authUser")
      if (!raw) return null
      const obj = JSON.parse(raw) as { role?: string }
      return obj?.role ?? null
    } catch {
      return null
    }
  }, [])
  const isAdmin = useMemo(() => {
    const r = (userRole || "").trim().toLowerCase()
    return r === "admin" || r === "super-admin"
  }, [userRole])
  const categoryFilterOptions = useMemo(() => [{ value: "", label: "All" }, ...getCategoryOptionsForUser(userRole).map((o) => ({ value: o.value, label: o.label }))], [userRole])
  const formTypeFilterOptions = useMemo(() => [{ value: "", label: "All" }, ...getFormTypeOptionsForUser(userRole).map((o) => ({ value: o.value, label: o.label }))], [userRole])

  const openUserDetailsModal = useCallback((userId: string) => {
    setUserDetailsModalOpen(true)
    setUserDetailsModalUser(null)
    setUserDetailsModalLoading(true)
    getUserApi(userId)
      .then((res) => {
        if (res?.success && res?.data) setUserDetailsModalUser(res.data as UserDetailsModalUser)
      })
      .catch(() => setUserDetailsModalUser(null))
      .finally(() => setUserDetailsModalLoading(false))
  }, [])

  const closeUserDetailsModal = useCallback(() => {
    setUserDetailsModalOpen(false)
    setUserDetailsModalUser(null)
  }, [])

  const handleAddNewListing = useCallback(() => {
    const gate = getPropertyDetailGateReason()
    if (gate === "login") {
      navigate("/login", { state: { from: { pathname: "/listings" } } })
      return
    }
    if (gate === "upload" || gate === "pending") {
      setKycModalVariant(gate)
      setKycModalOpen(true)
      return
    }
    navigate("/list-property")
  }, [navigate])

  useEffect(() => {
    props.setBreadcrumbItems("Listings", breadcrumbItems)
  }, [props])

  useEffect(() => {
    getStatesOptions().then(setStateOptions).catch(() => setStateOptions([]))
  }, [])

  useEffect(() => {
    if (!createdByFromUrl) return
    setFilters((f) => ({ ...f, createdBy: createdByFromUrl }))
    setAppliedFilters((a) => ({ ...a, createdBy: createdByFromUrl }))
  }, [createdByFromUrl])

  const fetchListings = useCallback(() => {
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    params.set("page", String(page))
    params.set("limit", String(LIMIT))
    if (appliedFilters.category) params.set("category", appliedFilters.category)
    if (appliedFilters.status) params.set("status", appliedFilters.status)
    if (appliedFilters.city) params.set("city", appliedFilters.city)
    if (appliedFilters.state) params.set("state", appliedFilters.state)
    if (appliedFilters.listingCategoryType) params.set("listingCategoryType", appliedFilters.listingCategoryType)
    if (appliedFilters.createdBy) params.set("createdBy", appliedFilters.createdBy)
    apiClient
      .get(`${GET_LISTINGS_URL}?${params.toString()}`)
      .then((res) => {
        const payload = res?.data
        const data = payload?.data ?? res?.data
        const list = Array.isArray(data) ? data : []
        setListings(list)
        if (payload?.pagination) setPagination(payload.pagination)
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err?.message || "Failed to load listings")
      })
      .finally(() => setLoading(false))
  }, [page, appliedFilters])

  useEffect(() => {
    fetchListings()
  }, [fetchListings])

  useEffect(() => {
    setImageViewerIndex(null)
    setDocumentViewer(null)
  }, [selectedListing?._id])

  const handleSearch = () => {
    setAppliedFilters(filters)
    setPage(1)
  }

  const handleReset = () => {
    const empty = { category: "", status: "", city: "", state: "", listingCategoryType: "", createdBy: "" }
    setFilters(empty)
    setAppliedFilters(empty)
    setPage(1)
    setSearchParams({})
  }

  const updateFilter = (key: string, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }))
  }

  const showDetail = (item: Listing) => {
    setSelectedListing(item)
  }

  const hideDetail = () => {
    setSelectedListing(null)
    setImageViewerIndex(null)
    setDocumentViewer(null)
  }

  const documentUrl = (url: string) =>
    url.startsWith("http") ? url : `${API_BASE.replace(/\/$/, "")}${url}`

  const isImageUrl = (url: string) => /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(url)

  const openInAppViewer = (url: string, title: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    setDocumentViewer({ url: documentUrl(url), title })
  }

  const handleRegenerateFlyer = (listingId: string) => {
    setGeneratingFlyerId(listingId)
    apiClient
      .post(`/api/listings/regenerate-flyer/${listingId}`)
      .then((res) => {
        const url = res?.data?.data?.generatedFlyerPdfUrl
        if (url) {
          setListings((prev) =>
            prev.map((l) => (l._id === listingId ? { ...l, generatedFlyerPdfUrl: url } : l)),
          )
          if (selectedListing?._id === listingId) {
            setSelectedListing((prev) => (prev ? { ...prev, generatedFlyerPdfUrl: url } : null))
          }
        }
      })
      .catch(() => { })
      .finally(() => setGeneratingFlyerId(null))
  }

  const formatDate = (d: string | undefined) => formatIndianDate(d)

  const formatListingArea = (item: { categorySpecific?: { superArea?: string | number }; superAreaSqFt?: number }) =>
    formatDisplayNumber(item.categorySpecific?.superArea ?? item.superAreaSqFt)

  const formatTableCellValue = (value: React.ReactNode): React.ReactNode => {
    if (value === undefined || value === null || value === "") return null
    if (typeof value === "number" && Number.isFinite(value)) return formatDisplayNumber(value)
    if (typeof value === "string" && isNumericDisplayValue(value)) return formatDisplayNumber(value)
    return value
  }

  const TableRow = ({ label, value }: { label: string; value: React.ReactNode }) => {
    const displayValue = formatTableCellValue(value)
    if (displayValue === null) return null
    return (
      <tr>
        <td className="listing-detail-table-label">{label}</td>
        <td className="listing-detail-table-value">{displayValue}</td>
      </tr>
    )
  }
  const SectionRow = ({ title }: { title: string }) => (
    <tr>
      <td colSpan={2} className="listing-detail-table-section">
        {title}
      </td>
    </tr>
  )

  const total = pagination.total ?? listings.length
  const totalPages = Math.max(1, pagination.totalPages ?? 1)
  const startIdx = total === 0 ? 0 : (pagination.page - 1) * (pagination.limit || LIMIT) + 1

  console.log(listings)

  return (
    <React.Fragment>
      <Row>
        <Col xs="12">
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h5" className="mb-0">All Listings</CardTitle>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{total} listing(s) total</span>
                <Button color="primary" size="sm" onClick={handleAddNewListing}>Add New</Button>
              </div>
            </CardHeader>
            <CardBody>
              <Row className="mb-3 g-3 align-items-end">
                <Col sm="6" md="3" lg="3">
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">Category</Label>
                    <Input type="select" value={filters.category} onChange={(e) => updateFilter("category", e.target.value)} bsSize="sm">
                      {categoryFilterOptions.map((o) => (
                        <option key={o.value || "all"} value={o.value}>{o.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col sm="6" md="3" lg="3">
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">Status</Label>
                    <Input type="select" value={filters.status} onChange={(e) => updateFilter("status", e.target.value)} bsSize="sm">
                      {statusOptions.map((o) => (
                        <option key={o.value || "all"} value={o.value}>{o.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col sm="6" md="3" lg="3">
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">City</Label>
                    <Input type="text" placeholder="City" value={filters.city} onChange={(e) => updateFilter("city", e.target.value)} bsSize="sm" />
                  </FormGroup>
                </Col>
                <Col sm="6" md="3" lg="3">
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">State</Label>
                    <Input type="select" value={filters.state} onChange={(e) => updateFilter("state", e.target.value)} bsSize="sm">
                      <option value="">All</option>
                      {stateOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col sm="6" md="3" lg="3">
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">Form Type</Label>
                    <Input type="select" value={filters.listingCategoryType} onChange={(e) => updateFilter("listingCategoryType", e.target.value)} bsSize="sm">
                      {formTypeFilterOptions.map((o) => (
                        <option key={o.value || "all"} value={o.value}>{o.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col sm="12" md="12" lg="12" className="mb-3 d-flex gap-2 justify-content-end">
                  <Button color="primary" size="sm" onClick={handleSearch}>
                    <i className="mdi mdi-magnify me-1" /> Search
                  </Button>
                  <Button color="secondary" size="sm" outline onClick={handleReset}>
                    <i className="mdi mdi-refresh me-1" /> Reset
                  </Button>
                </Col>
              </Row>
              {appliedFilters.createdBy && (
                <div className="alert alert-info py-2 mb-3 d-flex align-items-center justify-content-between">
                  <span>
                    <Badge color="info" className="me-2">Filter</Badge>
                    Showing listings by this user (from Dashboard).
                  </span>
                  <Button color="link" size="sm" className="p-0" onClick={handleReset}>Clear filter</Button>
                </div>
              )}
              {loading && <div className="alert alert-info">Loading listings...</div>}
              {error && <div className="alert alert-danger">{error}</div>}
              {!loading && !error && (
                <div className="table-responsive">
                  <Table className="table table-bordered align-middle mb-0">
                    <thead className="table-light text-nowrap">
                      <tr>
                        <th style={{ width: 50 }}>S.No.</th>
                        <th style={{ width: 180, minWidth: 180 }}>Action</th>
                        <th>Live Status</th>
                        <th>Property Title</th>
                        {/* <th>Form Type</th> */}
                        <th>Category</th>
                        <th>Occupancy Status</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Total Price</th>
                        <th>Area (sq.ft)</th>
                        <th>Listed by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listings.length === 0 ? (
                        <tr>
                          <td colSpan={12} className="text-center text-muted py-4">No listings found</td>
                        </tr>
                      ) : (
                        listings.map((item, idx) => (
                          <React.Fragment key={item._id}>
                            <tr>
                              <td>{(pagination.page - 1) * (pagination.limit || LIMIT) + idx + 1}</td>
                              <td style={{ width: 180, minWidth: 180, textAlign: "center" }}>
                                {selectedListing?._id === item._id ? (
                                  <div className="d-flex gap-1 flex-wrap justify-content-center">
                                    <Button color="secondary" className="text-nowrap" size="sm" onClick={hideDetail}>
                                      <i className="mdi mdi-close me-1" /> Hide
                                    </Button>
                                    <Button color="primary" size="sm" onClick={() => navigate(`/list-property/${selectedListing._id}`)}>
                                      <i className="mdi mdi-pencil me-1" /> Edit
                                    </Button>
                                    {isAdmin && (
                                      <Button color="danger" size="sm" disabled={generatingFlyerId === selectedListing._id} onClick={() => handleRegenerateFlyer(selectedListing._id)}>
                                        {generatingFlyerId === selectedListing._id ? <><i className="mdi mdi-loading mdi-spin me-1" /> Generating…</> : <><i className="mdi mdi-file-pdf-box me-1" /> {selectedListing.generatedFlyerPdfUrl ? "Regenerate flyer" : "Generate flyer"}</>}
                                      </Button>
                                    )}
                                    {selectedListing.generatedFlyerPdfUrl && (
                                      <a href={`${API_BASE}${selectedListing.generatedFlyerPdfUrl}`} target="_blank" rel="noopener noreferrer" className="btn btn-outline-danger btn-sm" download>
                                        <i className="mdi mdi-download me-1" /> Flyer
                                      </a>
                                    )}
                                  </div>
                                ) : (
                                  <div className="d-flex gap-1 flex-wrap justify-content-center">
                                    <Button color="primary" size="sm" onClick={() => showDetail(item)}>View more</Button>
                                    {item.generatedFlyerPdfUrl ? (
                                      <a href={`${API_BASE}${item.generatedFlyerPdfUrl}`} target="_blank" rel="noopener noreferrer" className="btn btn-danger btn-sm" download>
                                        <i className="mdi mdi-download me-1" /> Flyer
                                      </a>
                                    ) : isAdmin ? (
                                      <Button color="danger" size="sm" disabled={generatingFlyerId === item._id} onClick={() => handleRegenerateFlyer(item._id)}>
                                        {generatingFlyerId === item._id ? <><i className="mdi mdi-loading mdi-spin me-1" /> …</> : <><i className="mdi mdi-file-pdf-box me-1" /> Generate flyer</>}
                                      </Button>
                                    ) : null}
                                  </div>
                                )}
                              </td>
                              <td>
                                {(() => {
                                  const s = (item.status || "").toString().toLowerCase()
                                  if (s === "sold/deal closed" || s === "sold") return <Badge color="dark" className="fs-6">Sold</Badge>
                                  if (item.auctionStatus === "live") return <Badge color="success" className="fs-6">Live</Badge>
                                  if (item.isUpcoming || item.auctionStatus === "upcoming") return <Badge color="info">Upcoming</Badge>
                                  if (item.auctionStatus === "ended") return <Badge color="secondary">Ended</Badge>
                                  return "—"
                                })()}
                              </td>
                              <td><strong>{item.propertyTitle || "—"}</strong></td>
                              {/* <td><span className="text-muted small">{item.listingCategoryType || "—"}</span></td> */}
                              <td>{item.category || "—"}</td>
                              <td>
                                <Badge color={item.status === "Vacant" ? "success" : "secondary"} pill>
                                  {item.status || "—"}
                                </Badge>
                              </td>
                              <td>{item.city || "—"}</td>
                              <td>{item.state || "—"}</td>
                              <td>{(() => {
                                const salePrice = item.totalSalePrice ?? item.categorySpecific?.totalSalePrice
                                return salePrice != null && salePrice !== "" ? formatBidPrice(Number(salePrice)) : "—"
                              })()}</td>
                              <td>{formatListingArea(item)}</td>
                              <td className="text-nowrap">
                                {item.createdBy ? (
                                  <span title={item.createdBy.role || ""}>
                                    {canShowAdminForm(userRole) ? (
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 text-primary text-nowrap fw-medium text-start border-0"
                                        style={{ textDecoration: "none" }}
                                        title="View user details"
                                        onClick={() => openUserDetailsModal(item.createdBy!._id)}
                                      >
                                        {item.createdBy.fullName || "—"}
                                      </button>
                                    ) : (
                                      <span className="text-nowrap fw-medium">{item.createdBy.fullName || "—"}</span>
                                    )}
                                    <br />
                                    {item.createdBy.role && (
                                      <Badge color="secondary" className="ms-1" pill>{item.createdBy.role}</Badge>
                                    )}
                                  </span>
                                ) : (
                                  "—"
                                )}
                                <div className="mt-1" style={{ fontSize: "0.75rem", lineHeight: 1.3 }}>
                                  {item.createdAt && (
                                    <div className="text-muted">
                                      <i className="mdi mdi-plus-circle-outline me-1" />
                                      {formatIndianDateTime(item.createdAt)}
                                    </div>
                                  )}
                                  {item.updatedAt && item.updatedAt !== item.createdAt && (
                                    <div className="text-warning">
                                      <i className="mdi mdi-pencil-outline me-1" />
                                      {formatIndianDateTime(item.updatedAt)}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {selectedListing?._id === item._id && (
                              <tr>
                                <td colSpan={12} className="p-0 align-top border-top-0 listing-detail-expand-cell">
                                  <div className="listing-detail-card shadow-sm m-2 p-3">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                      <h6 className="mb-0 text-dark d-flex align-items-center">
                                        <i className="mdi mdi-home-city me-2 text-primary" />
                                        {selectedListing.propertyTitle || "Listing Details"}
                                      </h6>

                                    </div>
                                    <div className="listing-detail-metrics mb-3">
                                      <div className="listing-detail-metric">
                                        <div className="listing-detail-metric-label">Total Price</div>
                                        <div className="listing-detail-metric-value primary">
                                          {(() => {
                                            const salePrice = selectedListing.totalSalePrice ?? selectedListing.categorySpecific?.totalSalePrice
                                            return salePrice != null && salePrice !== "" ? formatBidPrice(Number(salePrice)) : "—"
                                          })()}
                                        </div>
                                      </div>
                                      <div className="listing-detail-metric">
                                        <div className="listing-detail-metric-label">Area</div>
                                        <div className="listing-detail-metric-value">
                                          {formatListingArea(item)}
                                        </div>
                                      </div>
                                      <div className="listing-detail-metric">
                                        <div className="listing-detail-metric-label">Status</div>
                                        <div className="listing-detail-metric-value">
                                          {(() => {
                                            const s = (selectedListing.status || "").toString().toLowerCase()
                                            const isSold = s === "sold/deal closed" || s === "sold"
                                            const displayStatus = isSold
                                              ? "Sold"
                                              : selectedListing.auctionStatus === "live"
                                                ? "Live"
                                                : selectedListing.auctionStatus === "ended"
                                                  ? "Ended"
                                                  : selectedListing.isUpcoming || selectedListing.auctionStatus === "upcoming"
                                                    ? "Upcoming"
                                                    : null
                                            if (!displayStatus) return "—"
                                            const badgeColor = displayStatus === "Live" ? "success" : displayStatus === "Ended" ? "secondary" : displayStatus === "Upcoming" ? "info" : "dark"
                                            return <Badge color={badgeColor} pill>{displayStatus}</Badge>
                                          })()}
                                        </div>
                                      </div>
                                      <div className="listing-detail-metric">
                                        <div className="listing-detail-metric-label">Location</div>
                                        <div className="listing-detail-metric-value">
                                          {[selectedListing.city, selectedListing.state].filter(Boolean).join(", ") || "—"}
                                        </div>
                                      </div>
                                    </div>
                                    <Row className="g-3 listing-detail-columns">
                                      <Col xs={12} lg={6} className="listing-detail-col">
                                        <table className="listing-detail-table">
                                          <tbody>
                                            <SectionRow title="Category & Status" />
                                            <TableRow label="Category" value={selectedListing.category} />
                                            <TableRow label="Status" value={selectedListing.status} />
                                            <TableRow label="Form Type" value={selectedListing.listingCategoryType} />
                                            <TableRow
                                              label="Listed by"
                                              value={selectedListing.createdBy ? `${selectedListing.createdBy.fullName || "—"} (${selectedListing.createdBy.role || "—"})` : "—"}
                                            />
                                            <SectionRow title="Property Status" />
                                            <TableRow label="Construction" value={selectedListing.constructionStatus} />
                                            <TableRow label="Furnishing" value={selectedListing.furnishingStatus} />
                                            <SectionRow title="Basic & Location" />
                                            <TableRow label="Property Title" value={selectedListing.propertyTitle} />
                                            <TableRow label="Building Name" value={selectedListing.buildingName} />
                                            <TableRow label="Address" value={selectedListing.address} />
                                            <TableRow label="Road Name" value={selectedListing.roadName} />
                                            <TableRow label="State" value={selectedListing.state} />
                                            <TableRow label="City" value={selectedListing.city} />
                                            <TableRow label="Pin Code" value={selectedListing.pinCode} />
                                            <TableRow label="Locality" value={selectedListing.microMarketLocality} />
                                            <TableRow label="Lat / Long" value={selectedListing.latitude != null && selectedListing.longitude != null ? `${selectedListing.latitude}, ${selectedListing.longitude}` : undefined} />
                                            <SectionRow title="Area & Unit" />
                                            <TableRow label="Super Area (sq.ft)" value={selectedListing.superAreaSqFt} />
                                            <TableRow label="Carpet Area (sq.ft)" value={selectedListing.carpetAreaSqFt} />
                                            <TableRow label="Total Carpet (sq.ft)" value={selectedListing.totalCarpetAreaSqFt} />
                                            <TableRow label="Input Method" value={selectedListing.carpetAreaInputMethod} />
                                            <TableRow label="Floor" value={selectedListing.floorNumber} />
                                            <TableRow label="Total Floors" value={selectedListing.totalFloorsInBuilding} />
                                            <TableRow label="Unit No" value={selectedListing.unitNo} />
                                            <TableRow label="Car Parking" value={selectedListing.isCarParkingIncluded} />
                                          </tbody>
                                        </table>
                                      </Col>
                                      <Col xs={12} lg={6} className="listing-detail-col">
                                        <table className="listing-detail-table">
                                          <tbody>
                                            <SectionRow title="Bidding" />
                                            <TableRow label="Bidding enabled" value={selectedListing.biddingEnabled ? "Yes" : "No"} />
                                            {selectedListing.biddingEnabled && (
                                              <>
                                                <TableRow label="Start price" value={selectedListing.biddingStartPrice != null ? formatBidPrice(Number(selectedListing.biddingStartPrice)) : undefined} />
                                                <TableRow label="Min increment" value={selectedListing.biddingMinIncrement != null ? formatBidPrice(Number(selectedListing.biddingMinIncrement)) : undefined} />
                                                <TableRow label="Auction start" value={selectedListing.auctionStartDate ? formatIndianDateTime(selectedListing.auctionStartDate) : undefined} />
                                                <TableRow label="Auction end" value={(selectedListing.auctionEndDate || selectedListing.biddingEndDate) ? formatIndianDateTime(selectedListing.auctionEndDate || selectedListing.biddingEndDate) : undefined} />
                                              </>
                                            )}
                                            <SectionRow title="Financial" />
                                            <TableRow label="Total Sale Price" value={selectedListing.totalSalePrice != null ? formatBidPrice(Number(selectedListing.totalSalePrice)) : undefined} />
                                            <TableRow label="Price per Sq.ft" value={selectedListing.pricePerSqFt != null ? formatBidPrice(Number(selectedListing.pricePerSqFt)) : undefined} />
                                            <TableRow label="Property Tax (Annual)" value={selectedListing.propertyTaxAnnual != null ? formatBidPrice(Number(selectedListing.propertyTaxAnnual)) : undefined} />
                                            <TableRow label="Monthly Maintenance" value={selectedListing.monthlyMaintenanceCharges != null ? formatBidPrice(Number(selectedListing.monthlyMaintenanceCharges)) : undefined} />
                                            <SectionRow title="Lease & Tenant" />
                                            <TableRow label="Lease Type" value={selectedListing.leaseType} />
                                            <TableRow label="Lease Start" value={formatDate(selectedListing.leaseStartDate)} />
                                            <TableRow label="Lease End" value={formatDate(selectedListing.leaseEndDate)} />
                                            <TableRow label="Duration (months)" value={selectedListing.leaseDurationMonths} />
                                            <TableRow label="Monthly Rent" value={selectedListing.monthlyRent != null ? formatBidPrice(Number(selectedListing.monthlyRent)) : undefined} />
                                            <TableRow label="Security Deposit" value={selectedListing.securityDeposit != null ? formatBidPrice(Number(selectedListing.securityDeposit)) : undefined} />
                                            <TableRow label="Occupancy" value={selectedListing.occupancyStatus} />
                                            <TableRow label="Lock-in (months)" value={selectedListing.lockInPeriodMonths} />
                                            <SectionRow title="Investment" />
                                            <TableRow label="Rental Yield (%)" value={selectedListing.expectedRentalYield} />
                                            <TableRow label="Annual Appreciation (%)" value={selectedListing.annualAppreciation} />
                                            <TableRow label="Cap Rate (%)" value={selectedListing.capRate} />
                                            <TableRow label="Investment Amount" value={selectedListing.investmentAmount != null ? formatBidPrice(Number(selectedListing.investmentAmount)) : undefined} />
                                            <TableRow label="Expected Return (Yrs)" value={selectedListing.expectedReturnYears} />
                                            <TableRow label="ROI (%)" value={selectedListing.roiPercentage} />
                                            <SectionRow title="Meta" />
                                            <TableRow label="Created" value={formatDate(selectedListing.createdAt)} />
                                            <TableRow label="Updated" value={formatDate(selectedListing.updatedAt)} />
                                          </tbody>
                                        </table>
                                      </Col>
                                    </Row>
                                    {(() => {
                                      const cat = selectedListing.categorySpecific as Record<string, unknown> | undefined
                                      const adminShort = cat?.["admin_shortPropertyDescription"]
                                      const adminDesc = cat?.["admin_propertyDescription"]
                                      const rawDesc = selectedListing.propertyDescription ?? cat?.["propertyDescription"]
                                      const hasShort = adminShort != null && String(adminShort).trim() !== ""
                                      const hasDesc = adminDesc != null && String(adminDesc).trim() !== ""
                                      const hasRaw = rawDesc != null && String(rawDesc).trim() !== ""
                                      if (!hasShort && !hasDesc && !hasRaw) return null
                                      return (
                                        <>
                                          {hasShort && (
                                            <div className="mt-3">
                                              <div className="listing-detail-metric-label mb-1">Short Description</div>
                                              <div className="listing-detail-desc">{decodeDraftRawToReact(String(adminShort))}</div>
                                            </div>
                                          )}
                                          {hasDesc && (
                                            <div className="mt-3">
                                              <div className="listing-detail-metric-label mb-1">Property Description</div>
                                              <div className="listing-detail-desc">{decodeDraftRawToReact(String(adminDesc))}</div>
                                            </div>
                                          )}
                                          {!hasDesc && hasRaw && (
                                            <div className="mt-3">
                                              <div className="listing-detail-metric-label mb-1">Description</div>
                                              <div className="listing-detail-desc">{decodeDraftRawToReact(String(rawDesc))}</div>
                                            </div>
                                          )}
                                        </>
                                      )
                                    })()}
                                    {selectedListing.categorySpecific && typeof selectedListing.categorySpecific === "object" && Object.keys(selectedListing.categorySpecific).length > 0 && (
                                      <div className="mt-3">
                                        <div className="listing-detail-metric-label mb-2">Category-specific details</div>
                                        <table className="listing-detail-table">
                                          <tbody>
                                            {Object.entries(selectedListing.categorySpecific)
                                              .filter(([k, v]) => !["propertyDescription", "admin_propertyDescription", "admin_shortPropertyDescription"].includes(k) && v != null && v !== "")
                                              .map(([k, v]) => {
                                                const isUrlOrPath = (s: string) => typeof s === "string" && (s.startsWith("/") || s.startsWith("http://") || s.startsWith("https://"))
                                                const linkFor = (urlOrPath: string) => (
                                                  <a href={documentUrl(urlOrPath)} target="_blank" rel="noopener noreferrer" className="text-primary">
                                                    {urlOrPath.split("/").pop() || "View file"}
                                                  </a>
                                                )
                                                const labelMap: Record<string, string> = {
                                                  rentRollMultiTenantedUrls: "Rent Roll (Multi Tenanted)",
                                                  rentRollMultiTenanted: "Rent Roll (Multi Tenanted)",
                                                }
                                                let displayVal: React.ReactNode
                                                const isDraftRichText = typeof v === "string" && v.trim().startsWith("{") && v.includes("blocks")
                                                if (isDraftRichText) {
                                                  displayVal = <div className="listing-detail-desc">{decodeDraftRawToReact(v as string)}</div>
                                                } else if (Array.isArray(v)) {
                                                  const items = v as unknown[]
                                                  if (items.every((item) => typeof item === "string" && isUrlOrPath(item as string))) {
                                                    displayVal = items.map((url, i) => <span key={i}>{i > 0 ? ", " : null}{linkFor(url as string)}</span>)
                                                  } else {
                                                    displayVal = items.map((item) => {
                                                      if (typeof item === "object" && item !== null) return JSON.stringify(item)
                                                      return formatCategoryFieldDisplay(k, item)
                                                    }).join(", ")
                                                  }
                                                } else if (typeof v === "string" && v.trim().startsWith("[")) {
                                                  try {
                                                    const parsed = JSON.parse(v)
                                                    if (Array.isArray(parsed) && parsed.every((item: unknown) => typeof item === "string" && isUrlOrPath(item))) {
                                                      displayVal = parsed.map((url: string, i: number) => <span key={i}>{i > 0 ? ", " : null}{linkFor(url)}</span>)
                                                    } else {
                                                      displayVal = Array.isArray(parsed)
                                                        ? parsed.map((item: unknown) => {
                                                          if (typeof item === "object" && item !== null) return JSON.stringify(item)
                                                          return formatCategoryFieldDisplay(k, item)
                                                        }).join(", ")
                                                        : formatCategoryFieldDisplay(k, v)
                                                    }
                                                  } catch {
                                                    displayVal = isUrlOrPath(v) ? linkFor(v) : v
                                                  }
                                                } else if (typeof v === "string" && isUrlOrPath(v)) {
                                                  displayVal = linkFor(v)
                                                } else if (typeof v === "object" && v !== null) {
                                                  displayVal = JSON.stringify(v)
                                                } else {
                                                  displayVal = formatCategoryFieldDisplay(k, v)
                                                }
                                                const label = labelMap[k] ?? k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())
                                                return (
                                                  <TableRow
                                                    key={k}
                                                    label={label}
                                                    value={displayVal}
                                                  />
                                                )
                                              })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                    {((selectedListing.propertyImagesUrls?.length ?? 0) > 0 || selectedListing.titleDeedUrl || selectedListing.titleDeedConveyanceDeedUrl || selectedListing.occupancyCertificateOcUrl || selectedListing.latestTaxReceiptsUrl || selectedListing.leaseAgreementUrl || selectedListing.vastuReportUrl || selectedListing.floorPlanUrl || selectedListing.floorPlanSiteLayoutUrl || selectedListing.buildingBrochureUrl || selectedListing.generatedFlyerPdfUrl || (selectedListing.propertyTaxReceiptsUrls?.length ?? 0) > 0) && (
                                      <div className="mt-3">
                                        <div className="listing-detail-metric-label mb-2">Media</div>
                                        <div className="listing-detail-media-section">
                                          {(selectedListing.propertyImagesUrls?.length ?? 0) > 0 && (
                                            <div className="mb-3">
                                              <div className="listing-detail-metric-label small mb-1">Property Images</div>
                                              <div className="d-flex flex-wrap gap-2">
                                                {selectedListing.propertyImagesUrls!.map((url, i) => (
                                                  <button
                                                    key={i}
                                                    type="button"
                                                    className="listing-detail-img-wrap border-0 bg-transparent p-0"
                                                    onClick={() => setImageViewerIndex(i)}
                                                    aria-label={`View image ${i + 1}`}
                                                  >
                                                    <img src={documentUrl(url)} alt={`Property ${i + 1}`} className="listing-detail-img rounded" />
                                                  </button>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {(selectedListing.floorPlanUrl || selectedListing.floorPlanSiteLayoutUrl || selectedListing.buildingBrochureUrl || selectedListing.generatedFlyerPdfUrl || selectedListing.titleDeedUrl || selectedListing.titleDeedConveyanceDeedUrl || selectedListing.occupancyCertificateOcUrl || selectedListing.latestTaxReceiptsUrl || (selectedListing.propertyTaxReceiptsUrls?.length ?? 0) > 0 || selectedListing.leaseAgreementUrl || selectedListing.vastuReportUrl) && (
                                            <div>
                                              <div className="listing-detail-metric-label small mb-1">Documents</div>
                                              <div className="d-flex flex-wrap gap-2">
                                                {selectedListing.generatedFlyerPdfUrl && (
                                                  <a href={`${API_BASE}${selectedListing.generatedFlyerPdfUrl}`} target="_blank" rel="noopener noreferrer" className="btn btn-outline-danger btn-sm" download>Flyer (PDF)</a>
                                                )}
                                                {selectedListing.floorPlanUrl && (
                                                  <Button type="button" color="outline-primary" size="sm" onClick={openInAppViewer(selectedListing.floorPlanUrl, "Floor Plan")}>Floor Plan</Button>
                                                )}
                                                {selectedListing.floorPlanSiteLayoutUrl && (
                                                  <Button type="button" color="outline-primary" size="sm" onClick={openInAppViewer(selectedListing.floorPlanSiteLayoutUrl, "Floor Plan / Site Layout")}>Floor Plan / Site Layout</Button>
                                                )}
                                                {selectedListing.buildingBrochureUrl && (
                                                  <Button type="button" color="outline-primary" size="sm" onClick={openInAppViewer(selectedListing.buildingBrochureUrl, "Building Brochure")}>Building Brochure</Button>
                                                )}
                                                {(selectedListing.titleDeedUrl || selectedListing.titleDeedConveyanceDeedUrl) && (
                                                  <Button type="button" color="outline-primary" size="sm" onClick={openInAppViewer(selectedListing.titleDeedUrl || selectedListing.titleDeedConveyanceDeedUrl!, "Title Deed")}>Title Deed</Button>
                                                )}
                                                {selectedListing.occupancyCertificateOcUrl && (
                                                  <Button type="button" color="outline-primary" size="sm" onClick={openInAppViewer(selectedListing.occupancyCertificateOcUrl, "Occupancy Certificate (OC)")}>Occupancy Certificate (OC)</Button>
                                                )}
                                                {selectedListing.latestTaxReceiptsUrl && (
                                                  <Button type="button" color="outline-primary" size="sm" onClick={openInAppViewer(selectedListing.latestTaxReceiptsUrl, "Tax Receipts")}>Tax Receipts</Button>
                                                )}
                                                {(selectedListing.propertyTaxReceiptsUrls?.length ?? 0) > 0 && selectedListing.propertyTaxReceiptsUrls!.map((url, i) => (
                                                  <Button key={i} type="button" color="outline-primary" size="sm" onClick={openInAppViewer(url, `Tax Receipt ${i + 1}`)}>Tax Receipt {i + 1}</Button>
                                                ))}
                                                {selectedListing.leaseAgreementUrl && (
                                                  <Button type="button" color="outline-primary" size="sm" onClick={openInAppViewer(selectedListing.leaseAgreementUrl, "Lease Agreement")}>Lease Agreement</Button>
                                                )}
                                                {selectedListing.vastuReportUrl && (
                                                  <Button type="button" color="outline-primary" size="sm" onClick={openInAppViewer(selectedListing.vastuReportUrl, "Vastu Report")}>Vastu Report</Button>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                        {selectedListing.propertyImagesUrls && selectedListing.propertyImagesUrls.length > 0 && imageViewerIndex !== null && (
                                          <Modal isOpen={imageViewerIndex !== null} toggle={() => setImageViewerIndex(null)} size="lg" className="listing-image-modal">
                                            <ModalHeader toggle={() => setImageViewerIndex(null)}>
                                              Property Image {imageViewerIndex + 1} of {selectedListing.propertyImagesUrls!.length}
                                            </ModalHeader>
                                            <ModalBody className="text-center position-relative">
                                              <Button color="light" size="sm" className="position-absolute start-0 top-50 translate-middle-y ms-2 z-1" disabled={imageViewerIndex <= 0} onClick={() => setImageViewerIndex((prev) => (prev ?? 0) - 1)}>
                                                <i className="mdi mdi-chevron-left" />
                                              </Button>
                                              <img src={documentUrl(selectedListing.propertyImagesUrls![imageViewerIndex])} alt={`Property ${imageViewerIndex + 1}`} className="img-fluid rounded" style={{ maxHeight: "70vh", objectFit: "contain" }} />
                                              <Button color="light" size="sm" className="position-absolute end-0 top-50 translate-middle-y me-2 z-1" disabled={imageViewerIndex >= selectedListing.propertyImagesUrls!.length - 1} onClick={() => setImageViewerIndex((prev) => (prev ?? 0) + 1)}>
                                                <i className="mdi mdi-chevron-right" />
                                              </Button>
                                            </ModalBody>
                                          </Modal>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {!loading && !error && total > 0 && totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <small className="text-muted">
                    Showing {startIdx}–{Math.min(startIdx + listings.length - 1, total)} of {total}
                  </small>
                  <Pagination className="mb-0 pagination-sm">
                    <PaginationItem disabled={page <= 1}>
                      <PaginationLink previous onClick={() => setPage((p) => Math.max(1, p - 1))} />
                    </PaginationItem>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <PaginationItem key={p} active={p === page}>
                        <PaginationLink onClick={() => setPage(p)}>{p}</PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem disabled={page >= totalPages}>
                      <PaginationLink next onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                    </PaginationItem>
                  </Pagination>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {documentViewer && (
        <Modal isOpen={!!documentViewer} toggle={() => setDocumentViewer(null)} size="xl" className="listing-document-modal">
          <ModalHeader toggle={() => setDocumentViewer(null)}>{documentViewer.title}</ModalHeader>
          <ModalBody className="p-0 d-flex align-items-center justify-content-center bg-light" style={{ minHeight: "70vh" }}>
            {isImageUrl(documentViewer.url) ? (
              <img src={documentViewer.url} alt={documentViewer.title} className="img-fluid rounded" style={{ maxHeight: "75vh", objectFit: "contain" }} />
            ) : (
              <iframe title={documentViewer.title} src={documentViewer.url} className="border-0 w-100 rounded" style={{ minHeight: "70vh" }} />
            )}
          </ModalBody>
        </Modal>
      )}

      <UserDetailsModal
        isOpen={userDetailsModalOpen}
        toggle={closeUserDetailsModal}
        user={userDetailsModalUser}
        loading={userDetailsModalLoading}
      />

      <KycRequiredModal
        isOpen={kycModalOpen}
        toggle={() => setKycModalOpen(false)}
        variant={kycModalVariant}
      />
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(Listings)
